import { TurnStateEnum } from '../Enums/TurnStateEnum';
import { CreateGameRequest, CreateGameResponse } from '../socket_messages/CreateGame';
import { CreateNewTurnRequest } from '../socket_messages/CreateNewTurn';
import { EndTurnRequest } from '../socket_messages/EndTurn';
import { CheckGameExistsRequest } from '../socket_messages/GameExistsCheck';
import { GetPlayerRequest, GetPlayerResponse } from '../socket_messages/GetPlayer';
import { GetPlayersRequest, GetPlayersResponse } from '../socket_messages/GetPlayers';
import { JoinGameResponse, JoinGameRequest } from '../socket_messages/JoinGame';
import { LeaveGameRequest } from '../socket_messages/LeaveGame';
import { SynchronizeGameValuesResponse, SynchronizeGameValuesRequest } from '../socket_messages/SynchronizeGameValues';
import { UpdatePlayersBetsRequest } from '../socket_messages/UpdatePlayersBets';
import { RemovePlayerBetRequest } from '../socket_messages/RemovePlayerBet';
import { UpdateTurnHolderBetRequest } from '../socket_messages/UpdateTurnHolderBet';
import { UpdateTurnStateRequest } from '../socket_messages/UpdateTurnStateRequest';
import { StartGameRequest } from '../socket_messages/StartGame';
import { UpdatePlayerTokensRequest } from '../socket_messages/UpdatePlayerTokens';
import { SelectPlaylistRequest } from '../socket_messages/SelectPlaylist';
import { ConfirmPlayerReadyRequest } from '../socket_messages/ConfirmPlayerReady';
import { CancelPlayerReadyRequest } from '../socket_messages/CancelPlayerReady';
import { SetCardsToWinRequest } from '../socket_messages/SetCardsToWin';

export class AppGameContextHttpClient {
    private gameId: string;
    private playerId: string;

    constructor(gameId: string, playerId: string) {
        this.gameId = gameId;
        this.playerId = playerId;
    }

    LeaveGame = (): Promise<HttpResult> => {
        return this.CallGameContext(LeaveGameRequest.Message);
    };

    SynchronizeGameValues = (): Promise<HttpResultValue<SynchronizeGameValuesResponse>> => {
        return this.CallGameContextWithValue(SynchronizeGameValuesRequest.Message);
    };

    GetPlayer = (): Promise<HttpResultValue<GetPlayerResponse>> => {
        return this.CallGameContextWithValue(GetPlayerRequest.Message);
    };

    GetPlayers = (): Promise<HttpResultValue<GetPlayersResponse>> => {
        return this.CallGameContextWithValue(GetPlayersRequest.Message);
    };

    UpdateTurnState = (currentTurnState: TurnStateEnum, wantedTurnState: TurnStateEnum): Promise<HttpResult> => {
        return this.CallGameContext(`${UpdateTurnStateRequest.Message}?currentTurnState=${currentTurnState}&wantedTurnState=${wantedTurnState}`);
    };

    UpdateTurnHolderBet = (turnHolderBet: string): Promise<HttpResult> => {
        return this.CallGameContext(`${UpdateTurnHolderBetRequest.Message}?turnHolderBet=${turnHolderBet}`);
    };

    UpdateOtherPlayersBets = (playerBetToAdd: string): Promise<HttpResult> => {
        return this.CallGameContext(`${UpdatePlayersBetsRequest.Message}?playerBetToAdd=${playerBetToAdd}`);
    };

    RemovePlayerBet = (): Promise<HttpResult> => {
        return this.CallGameContext(RemovePlayerBetRequest.Message);
    };

    EndTurn = (): Promise<HttpResult> => {
        return this.CallGameContext(`${EndTurnRequest.Message}`);
    };

    CreateNewTurn = (): Promise<HttpResult> => {
        return this.CallGameContext(`${CreateNewTurnRequest.Message}`);
    };

    StartGame = (): Promise<HttpResult> => {
        return this.CallGameContext(`${StartGameRequest.Message}`);
    };

    SelectPlaylist = (playlist: string): Promise<HttpResult> => {
        return this.CallGameContext(`${SelectPlaylistRequest.Message}?playlist=${encodeURIComponent(playlist)}`);
    };

    ConfirmPlayerReady = (): Promise<HttpResult> => {
        return this.CallGameContext(ConfirmPlayerReadyRequest.Message);
    };

    CancelPlayerReady = (): Promise<HttpResult> => {
        return this.CallGameContext(CancelPlayerReadyRequest.Message);
    };

    UpdatePlayerTokens = (targetPlayerId: string, tokenDelta: number): Promise<HttpResult> => {
        return this.CallGameContext(`${UpdatePlayerTokensRequest.Message}?targetPlayerId=${targetPlayerId}&tokenDelta=${tokenDelta}`);
    };

    SetCardsToWin = (cardsToWin: number): Promise<HttpResult> => {
        return this.CallGameContext(`${SetCardsToWinRequest.Message}?cardsToWin=${cardsToWin}`);
    };

    private CallGameContextWithValue = <T>(url: string): Promise<HttpResultValue<T>> => {
        return AppClient.CallWithResponseValue<T>(url, {
            gameId: this.gameId,
            playerId: this.playerId
        });
    };

    private CallGameContext = (url: string): Promise<HttpResult> => {
        return AppClient.Call(url, {
            gameId: this.gameId,
            playerId: this.playerId
        });
    };
}

export class AppSocketContextHttpClient {
    private playerId: string;

    constructor(playerId: string) {
        this.playerId = playerId;
    }

    CreateGame = async (playerName: string): Promise<HttpResultValue<CreateGameResponse>> => {
        return AppClient.CallWithResponseValue(`${CreateGameRequest.Message}?playerName=${playerName}`, {
            playerId: this.playerId
        });
    };

    JoinGame = async (gameId: string, playerName: string): Promise<HttpResultValue<JoinGameResponse>> => {
        return AppClient.CallWithResponseValue(`${JoinGameRequest.Message}?playerName=${playerName}`, {
            gameId: gameId,
            playerId: this.playerId
        });
    };
}

export class AppClient {
    private static baseUrl = process.env.REACT_APP_API_URL;

    static CheckGameExists = async (gameId: string): Promise<HttpResult> => {
        return await AppClient.Call(`${CheckGameExistsRequest.Message}?gameId=${gameId}`);
    };

    static async Call(url: string, headers: Record<string, string> = {}): Promise<HttpResult> {
        try {
            var response = await fetch(`${AppClient.baseUrl}/${url}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                }
            });

            if (response.ok) {
                return {
                    success: true
                };
            }

            const result: string = await response.json();
            return {
                success: false,
                errorMessage: result
            };
        } catch (error) {
            return {
                success: false,
                errorMessage: 'Un problème est survenu lors du call HTTP'
            };
        }
    }

    static async CallWithResponseValue<T>(url: string, headers: Record<string, string> = {}): Promise<HttpResultValue<T>> {
        try {
            var response = await fetch(`${AppClient.baseUrl}/${url}`, {
                method: 'GET',
                headers: {
                    ...headers
                }
            });

            if (!response.ok) {
                const result: string = await response.json();
                return {
                    success: false,
                    errorMessage: result
                };
            }

            const result: T = await response.json();
            return {
                success: true,
                value: result
            };
        } catch (error) {
            return {
                success: false,
                errorMessage: 'Un problème est survenu lors du call HTTP'
            };
        }
    }
}

export type HttpResult = HttpError | HttpResultBasic;

export type HttpResultValue<T> = HttpError | HttpResultWithValue<T>;

type HttpError = {
    success: false;
    errorMessage: string;
};

type HttpResultWithValue<T> = {
    value: T;
} & HttpResultBasic;

type HttpResultBasic = {
    success: true;
};
